import { useMemo } from "react";
import classNames from "classnames";
import { useTable, useSortBy, Column } from "react-table";
import { SummonerSpell } from "@outplayed/tooltips";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { TableSortLabel } from "@ugg/shared/components/common/TableSortLabel";
import { round, numberWithCommas } from "@ugg/shared/utils/math";
import { AdvBuildInfo } from "@ugg/shared/api/data-parser/champions/adv-builds";

interface SpellsTableProps {
  className?: string;
  data: AdvBuildInfo["summoner_spells"];
}

export function SpellsTable(props: SpellsTableProps) {
  const { className, data } = props;

  const totalMatches = data.reduce((acc, curr) => {
    return (acc += curr.matches);
  }, 0);

  const columns = useMemo<Column<AdvBuildInfo["summoner_spells"][number]>[]>(
    () => [
      {
        accessor: "ids",
        className: "flex-1",
        Cell: ({ cell: { value } }) => {
          return (
            <div className="flex items-center gap-[6px]">
              <SummonerSpell className="flex-none w-[30px] h-[30px] rounded-[3px]" spellId={value[0]} ssr />
              <SummonerSpell className="flex-none w-[30px] h-[30px] rounded-[3px]" spellId={value[1]} ssr />
            </div>
          );
        },
      },
      {
        accessor: "win_rate",
        className: "w-[60px] text-center",
        headerClassName: "w-[60px] whitespace-nowrap",
        Header: <div>Win Rate</div>,
        Cell: (props) => {
          return <div className="text-[14px] font-medium text-lavender-50">{`${round(props.value, 2)}% `}</div>;
        },
        sortDescFirst: true,
      },
      {
        accessor: "matches",
        className: "w-[65px] text-center",
        headerClassName: "w-[65px] whitespace-nowrap",
        Header: <div>Pick Rate</div>,
        Cell: ({ cell: { value } }) => {
          return (
            <div>
              <div className="text-white text-[14px] font-bold">{`${round((value / totalMatches) * 100, 2)}% `}</div>
              <div className="text-[12px] font-medium text-lavender-50">{numberWithCommas(value)}</div>
            </div>
          );
        },
        sortDescFirst: true,
      },
    ],
    [data],
  );

  const { state, getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [{ id: "matches", desc: true }],
      },
      disableSortRemove: true,
    },
    useSortBy,
  );

  return (
    <div className={classNames("rounded-[3px] overflow-hidden", className)}>
      <div className="flex items-center justify-between p-[18px] bg-purple-200">
        <HeaderBullet>Summoner Spells</HeaderBullet>
        <div {...getTableProps({ className: "" })}>
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps({ className: "flex items-center gap-[24px]" })}>
              {headerGroup.headers.map((column) => {
                return (
                  <div className="contents" {...column.getHeaderProps(column.getSortByToggleProps({}))}>
                    <TableSortLabel
                      className={classNames(
                        "flex items-center justify-center text-[14px]",
                        {
                          "text-white font-semibold": column.isSorted,
                          "text-lavender-50 font-medium": !column.isSorted,
                        },
                        column.headerClassName,
                      )}
                      active={column.isSorted}
                      direction={column.isSortedDesc ? "desc" : "asc"}
                      labelOffset={5}
                    >
                      {column.render("Header")}
                    </TableSortLabel>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div {...getTableBodyProps({ className: "" })}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <div
              {...row.getRowProps({
                className: "flex items-center gap-[24px] px-[18px] py-[12px] border-t-[1px] border-purple-500 bg-purple-200",
              })}
            >
              {row.cells.map((cell) => {
                const tdClassNames = classNames(cell.column.className);
                return <div {...cell.getCellProps({ className: tdClassNames })}>{cell.render("Cell", { index: i })}</div>;
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
